import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { ShieldCheck, CheckCircle, Award, Star, Clock, Zap } from 'lucide-react';

/**
 * Fetches brand intelligence trust data and merges with businessSettings
 * to produce an array of trust badge items for ConfigurableHero.
 *
 * Falls back to hardcoded badges from businessSettings if brand intelligence
 * data is not configured.
 */
export function useBrandTrustBadges(businessSettings: {
  license_number?: string | null;
  years_experience?: number | null;
} | null) {
  const { data: brandIntel } = useQuery({
    queryKey: ['brand-intelligence-badges'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('brand_intelligence')
        .select('credentials, trust_signals, value_props, custom_badges, availability')
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  const badges: { icon: React.ReactNode; label: string }[] = [];

  // ── Brand Intelligence badges ──
  if (brandIntel) {
    // Credentials (e.g., "EPA Certified", "NATE Certified")
    const credentials = Array.isArray(brandIntel.credentials) ? brandIntel.credentials : [];
    credentials.slice(0, 2).forEach((cred: string) => {
      badges.push({ icon: React.createElement(Award, { className: 'h-4 w-4' }), label: cred });
    });

    // Trust signals (e.g., "100% Satisfaction Guarantee", "Background-checked techs")
    const trustSignals = Array.isArray(brandIntel.trust_signals) ? brandIntel.trust_signals : [];
    trustSignals.slice(0, 2).forEach((sig: string) => {
      badges.push({ icon: React.createElement(ShieldCheck, { className: 'h-4 w-4' }), label: sig });
    });

    // Custom badges (e.g., "Family Owned", "BBB A+ Rated")
    const customBadges = Array.isArray(brandIntel.custom_badges) ? brandIntel.custom_badges : [];
    customBadges.slice(0, 2).forEach((badge: string) => {
      badges.push({ icon: React.createElement(Star, { className: 'h-4 w-4' }), label: badge });
    });

    // Availability (e.g., "24/7 Emergency Service")
    const availability = brandIntel.availability as Record<string, unknown> | null;
    if (availability && (availability as any)?.emergency_service) {
      badges.push({
        icon: React.createElement(Clock, { className: 'h-4 w-4' }),
        label: '24/7 Emergency Service',
      });
    }
  }

  // ── Fallback from businessSettings ──
  if (badges.length === 0 && businessSettings) {
    if (businessSettings.license_number) {
      badges.push({
        icon: React.createElement(ShieldCheck, { className: 'h-4 w-4' }),
        label: 'Licensed & Insured',
      });
    }
    if (businessSettings.years_experience) {
      badges.push({
        icon: React.createElement(CheckCircle, { className: 'h-4 w-4' }),
        label: `${businessSettings.years_experience}+ Years Experience`,
      });
    }
    // Always add at least one trust badge
    if (badges.length === 0) {
      badges.push({
        icon: React.createElement(CheckCircle, { className: 'h-4 w-4' }),
        label: 'Licensed & Insured',
      });
    }
  }

  // Limit to 4 badges max for layout reasons
  return badges.slice(0, 4);
}
